import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Modal, Spinner, Table } from 'react-bootstrap';
import { createOrderReceiptRead } from '../../clients/order_client';
import { logInfo } from '../../config_and_helpers/logger';
import { orderLineLabelForReceipt } from '../../utils/receiptRead';

const LOG_CONTEXT = 'ReceiptReadReview';

/**
 * Text for a decimal field. Null and blank stay empty.
 *
 * @param {string|null|undefined} value
 * @returns {string}
 */
function displayDecimal(value) {
  if (value == null || value === '') return '—';
  return String(value);
}

/**
 * @param {'matched'|'unmatched'|'ambiguous'|string} match
 * @returns {string}
 */
function matchBadgeBg(match) {
  if (match === 'matched') return 'success';
  if (match === 'ambiguous') return 'warning';
  return 'secondary';
}

/**
 * @param {string|undefined} match
 * @returns {string}
 */
function matchLabel(match) {
  if (match === 'matched') return 'Matched';
  if (match === 'unmatched') return 'Unmatched';
  if (match === 'ambiguous') return 'Ambiguous';
  return match || '—';
}

/**
 * One stored receipt review. Does not write stock or received lots.
 *
 * @param {Object} props
 * @param {Object} props.record - Receipt-read response body
 * @param {Array} [props.orderItems]
 * @param {boolean} [props.showMatch] - Order-line match column. Off for a scan that has no order.
 */
export function ReceiptReadRecord({ record, orderItems = [], showMatch = true }) {
  if (!record) return null;

  if (record.status === 'failed') {
    return (
      <Alert variant="danger" className="mb-0">
        {record.error_message || 'The receipt could not be read.'}
      </Alert>
    );
  }

  const lines = Array.isArray(record.lines) ? record.lines : [];
  const warnings = Array.isArray(record.warnings) ? record.warnings : [];

  return (
    <div>
      <p className="mb-1">
        <span className="fw-semibold">Invoice</span>{' '}
        {record.invoice_number || '—'}
      </p>
      <p className="mb-2">
        <span className="fw-semibold">Supplier</span>{' '}
        {record.supplier_name || '—'}
      </p>
      {warnings.length > 0 && (
        <Alert variant="warning">
          {warnings.map((warning, index) => (
            <div key={`${index}-${warning}`}>{String(warning)}</div>
          ))}
        </Alert>
      )}
      {lines.length === 0 ? (
        <p className="text-muted mb-0">No lines were read.</p>
      ) : (
        <div className="glass-table-wrap">
          <Table hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>Catalogue</th>
                <th>Description</th>
                <th className="text-end">Qty</th>
                <th>Unit</th>
                <th className="text-end">Price</th>
                <th>Batch</th>
                <th>Expiry</th>
                {showMatch ? <th>Match</th> : null}
              </tr>
            </thead>
            <tbody>
              {lines.map(line => {
                const lineLabel =
                  line.match === 'matched'
                    ? orderLineLabelForReceipt(orderItems, line.order_item_id)
                    : '';
                return (
                  <tr key={line.id ?? `${line.catalog_number}-${line.description}`}>
                    <td>{line.catalog_number || '—'}</td>
                    <td>{line.description || '—'}</td>
                    <td className="text-end">{displayDecimal(line.quantity)}</td>
                    <td>{line.unit || '—'}</td>
                    <td className="text-end">{displayDecimal(line.unit_price)}</td>
                    <td>{line.batch || '—'}</td>
                    <td>{line.expiry || '—'}</td>
                    {showMatch ? (
                      <td>
                        <Badge bg={matchBadgeBg(line.match)}>
                          {matchLabel(line.match)}
                        </Badge>
                        {line.match === 'matched' ? (
                          <div className="small mt-1">
                            {lineLabel || `Order line ${line.order_item_id}`}
                          </div>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

/**
 * Loads one or more receipt reads and shows them in a Procure glass dialog.
 * A second read stores another review and leaves the earlier one in place.
 *
 * @param {Object} props
 * @param {boolean} props.show
 * @param {Function} props.onHide
 * @param {string} props.token
 * @param {number|string} props.orderId
 * @param {Array<number|string>} props.imageIds
 * @param {Array} [props.orderItems]
 */
const ReceiptReadReviewModal = ({
  show,
  onHide,
  token,
  orderId,
  imageIds = [],
  orderItems = [],
}) => {
  const [loading, setLoading] = useState(false);
  const [rereadingId, setRereadingId] = useState(null);
  const [records, setRecords] = useState([]);
  const imageKey = imageIds.map(id => String(id)).join(',');

  useEffect(() => {
    if (!show || !token || orderId == null || !imageIds.length) return undefined;

    let cancelled = false;
    setLoading(true);
    setRecords([]);
    logInfo(LOG_CONTEXT, 'read receipts', {
      orderId,
      imageIds,
    });

    Promise.all(
      imageIds.map(imageId =>
        createOrderReceiptRead(token, orderId, imageId).then(result => ({
          imageId,
          result,
        }))
      )
    ).then(rows => {
      if (cancelled) return;
      setRecords(rows);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
    // imageKey stands in for imageIds so a new array with the same ids does not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, token, orderId, imageKey]);

  const readAgain = imageId => {
    setRereadingId(imageId);
    logInfo(LOG_CONTEXT, 'read receipt again', { orderId, orderImageId: imageId });
    createOrderReceiptRead(token, orderId, imageId).then(result => {
      setRecords(prev =>
        prev.map(row =>
          String(row.imageId) === String(imageId) ? { imageId, result } : row
        )
      );
      setRereadingId(null);
    });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      backdrop="static"
      size="lg"
      dialogClassName="procure-modal-glass procure-modal-glass-form"
    >
      <Modal.Header closeButton className="procure-modal__header">
        <Modal.Title>Receipt review</Modal.Title>
      </Modal.Header>
      <Modal.Body className="procure-modal__body">
        <p className="text-muted small">
          This read is not applied to stock. Confirm the receipt on the order
          as usual.
        </p>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Reading receipt</span>
            </Spinner>
          </div>
        ) : (
          records.map(({ imageId, result }) => (
            <div key={String(imageId)} className="glass-panel mb-3">
              <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                <h3 className="h6 mb-0">Receipt file {imageId}</h3>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => readAgain(imageId)}
                  disabled={rereadingId != null}
                >
                  {String(rereadingId) === String(imageId)
                    ? 'Reading…'
                    : 'Read again'}
                </Button>
              </div>
              {result?.disabled ? (
                <Alert variant="info" className="mb-0">
                  Receipt reading is turned off.
                </Alert>
              ) : result?.success ? (
                <ReceiptReadRecord record={result.data} orderItems={orderItems} />
              ) : (
                <Alert variant="danger" className="mb-0">
                  {(result?.messages || []).join(' ') ||
                    'Could not read this receipt.'}
                </Alert>
              )}
            </div>
          ))
        )}
      </Modal.Body>
      <Modal.Footer className="procure-modal__footer">
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReceiptReadReviewModal;
